import classNames from "classnames";
import * as React from "react";

import { Check, Pencil } from "@/shared/foundation/icons";
import { Tooltip } from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http";

import { RobotoMarkdown } from "../RobotoMarkdown";

import styles from "./InlineEdit.module.css";

export interface InlineEditProps {
  value: string;
  /** Saves the trimmed text. A rejection keeps the editor open with the error. */
  onSave: (next: string) => Promise<unknown>;
  /**
   * `false` for a name (Enter saves), `true` for a description (Enter is a
   * new line), which is shown as Markdown.
   */
  multiline?: boolean;
  /** Caps the text; a multiline field also shows how much is used. */
  maxLength?: number;
  /** An empty value is refused with `requiredMessage` rather than saved. */
  required?: boolean;
  /** @default "Name is required" */
  requiredMessage?: string;
  /** In the field while it's empty, and in place of an empty value. */
  placeholder?: string;
  /** Matches the displayed text, so nothing moves on entering edit. */
  textStyle: "title" | "body";
  /** What is edited, as "dataset name": the edit button is "Edit {ariaLabel}". */
  ariaLabel: string;
  /** Shows the value without offering to edit it. */
  readOnly?: boolean;
  /**
   * Controls editing from outside, as an "Add description" link does. Given,
   * the field is controlled: it opens and closes only as `editing` says, and
   * asks for a change through `onEditingChange`. Left out, it keeps its own
   * state.
   */
  editing?: boolean;
  /** Called with whether to edit, when the field opens or closes. */
  onEditingChange?: (editing: boolean) => void;
  /**
   * Fills its container rather than hugging the text, as in a table cell,
   * whose padding then places the field.
   */
  fill?: boolean;
}

type CloseResult = { saved?: string; keyboard: boolean };

/**
 * A header's name or description, edited in place. Both look alike: a 2px
 * primary border at the text's own size, nothing under it but an error, no Save or
 * Cancel. Enter (a name) or ⌘↵ saves, so does clicking away; Esc restores the
 * value. While saving, the new text stays; after, "✓ Saved" shows for 1.5s.
 * A failed save keeps the text and the editor open, with Retry.
 */
export function InlineEdit({
  value,
  onSave,
  multiline = false,
  maxLength,
  required = false,
  requiredMessage = "Name is required",
  placeholder,
  textStyle,
  ariaLabel,
  readOnly = false,
  editing: editingProp,
  onEditingChange,
  fill = false,
}: InlineEditProps) {
  // Controlled when `editing` is given, and then only through
  // `onEditingChange`; otherwise it keeps its own state.
  const [ownEditing, setOwnEditing] = React.useState(false);
  const controlled = editingProp !== undefined;
  const editing = controlled ? editingProp : ownEditing;
  const setEditing = (next: boolean) => {
    if (!controlled) {
      setOwnEditing(next);
    }
    onEditingChange?.(next);
  };

  // The text just saved, shown until the record catches up.
  const [pending, setPending] = React.useState<string | null>(null);
  React.useEffect(() => setPending(null), [value]);
  const current = pending ?? value;

  const [justSaved, setJustSaved] = React.useState(false);
  React.useEffect(() => {
    if (!justSaved) {
      return;
    }
    const timer = window.setTimeout(() => setJustSaved(false), 1500);
    return () => window.clearTimeout(timer);
  }, [justSaved]);

  const editButtonRef = React.useRef<HTMLButtonElement>(null);
  const returnFocus = React.useRef(false);
  React.useEffect(() => {
    if (!editing && returnFocus.current) {
      returnFocus.current = false;
      editButtonRef.current?.focus();
    }
  }, [editing]);

  const textClass = textStyle === "title" ? styles.title : styles.body;

  if (editing && !readOnly) {
    return (
      <Editor
        initial={current}
        onSave={onSave}
        multiline={multiline}
        maxLength={maxLength}
        required={required}
        requiredMessage={requiredMessage}
        placeholder={placeholder}
        textClass={textClass}
        ariaLabel={ariaLabel}
        fill={fill}
        onClose={({ saved, keyboard }: CloseResult) => {
          if (saved !== undefined) {
            setPending(saved);
            setJustSaved(true);
          }
          // Clicking away leaves focus where the click put it.
          returnFocus.current = keyboard;
          setEditing(false);
        }}
      />
    );
  }

  const savedNote = justSaved && (
    <span className={styles.saved} role="status">
      <Check aria-hidden className={styles.savedIcon} />
      Saved
    </span>
  );

  if (multiline) {
    return (
      <MultilineDisplay
        text={current}
        placeholder={placeholder}
        textClass={textClass}
        ariaLabel={ariaLabel}
        onEdit={readOnly ? undefined : () => setEditing(true)}
        editButtonRef={editButtonRef}
        savedNote={savedNote}
      />
    );
  }

  const shown = current || (
    <span className={styles.placeholder}>{placeholder}</span>
  );
  if (readOnly) {
    return (
      <span className={classNames(styles.surface, textClass)}>{shown}</span>
    );
  }
  return (
    <span className={styles.titleDisplay}>
      <button
        ref={editButtonRef}
        type="button"
        className={classNames(styles.surface, styles.editable, textClass)}
        aria-label={`Edit ${ariaLabel}${current ? `: ${current}` : ""}`}
        onClick={() => setEditing(true)}
      >
        <span className={styles.titleText}>{shown}</span>
        <Pencil aria-hidden className={styles.pencil} />
      </button>
      {savedNote}
    </span>
  );
}

/**
 * Text rendered as Markdown, clamped to four lines until "Show more", which
 * expands it in place; "Show more" shows only when the text overflows. A
 * pencil on hover edits it, as does a click anywhere but on a link.
 */
function MultilineDisplay({
  text,
  placeholder,
  textClass,
  ariaLabel,
  onEdit,
  editButtonRef,
  savedNote,
}: {
  text: string;
  placeholder?: string;
  textClass: string;
  ariaLabel: string;
  onEdit?: () => void;
  editButtonRef: React.Ref<HTMLButtonElement>;
  savedNote: React.ReactNode;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const [overflows, setOverflows] = React.useState(false);
  const textRef = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    const element = textRef.current;
    if (!element || expanded) {
      return;
    }
    const measure = () =>
      setOverflows(element.scrollHeight > element.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [expanded, text]);

  return (
    <div
      className={classNames(styles.surface, styles.multilineDisplay, {
        [styles.editable]: onEdit,
      })}
      data-testid="entity-header-description"
      // A click on the text edits it too, unless it follows a link; keyboard
      // users have the Edit button.
      onClick={(event) => {
        if (!(event.target as Element).closest("a")) {
          onEdit?.();
        }
      }}
    >
      <div className={styles.multilineBody}>
        <div
          ref={textRef}
          className={classNames(
            textClass,
            expanded ? styles.multilineText : styles.clamped,
          )}
        >
          {text ? (
            <RobotoMarkdown content={text} className={styles.markdown} />
          ) : (
            <span className={styles.placeholder}>{placeholder}</span>
          )}
        </div>
      </div>
      {/* Beside the text's last line, not under it, so a long description
          takes no more height than its four lines. */}
      {(overflows || expanded) && (
        <button
          type="button"
          className={classNames(styles.quietLink, styles.showMore)}
          onClick={(event) => {
            event.stopPropagation();
            setExpanded((value) => !value);
          }}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
      {savedNote}
      {onEdit && (
        <Tooltip>
          <Tooltip.Trigger
            delay={250}
            render={
              <button
                ref={editButtonRef}
                type="button"
                className={styles.editButton}
                aria-label={`Edit ${ariaLabel}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onEdit();
                }}
              />
            }
          >
            <Pencil aria-hidden className={styles.editIcon} />
          </Tooltip.Trigger>
          <Tooltip.Content>Edit {ariaLabel}</Tooltip.Content>
        </Tooltip>
      )}
    </div>
  );
}

function Editor({
  initial,
  onSave,
  multiline,
  maxLength,
  required,
  requiredMessage,
  placeholder,
  textClass,
  ariaLabel,
  fill,
  onClose,
}: {
  initial: string;
  onSave: (next: string) => Promise<unknown>;
  multiline: boolean;
  maxLength?: number;
  required: boolean;
  requiredMessage: string;
  placeholder?: string;
  textClass: string;
  ariaLabel: string;
  fill: boolean;
  onClose: (result: CloseResult) => void;
}) {
  const [draft, setDraft] = React.useState(initial);
  const [saving, setSaving] = React.useState(false);
  const [missing, setMissing] = React.useState(false);
  const [error, setError] = React.useState<React.ReactNode>(null);
  const closed = React.useRef(false);
  const inFlight = React.useRef(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // A description grows with its text rather than scrolling.
  React.useLayoutEffect(() => {
    const element = textareaRef.current;
    if (element) {
      element.style.height = "auto";
      element.style.height = `${element.scrollHeight}px`;
    }
  }, [draft]);

  const close = (result: CloseResult) => {
    closed.current = true;
    onClose(result);
  };

  const save = async (keyboard: boolean) => {
    if (closed.current || inFlight.current) {
      return;
    }
    const next = draft.trim();
    if (next === initial.trim()) {
      close({ keyboard });
      return;
    }
    if (required && next === "") {
      setMissing(true);
      return;
    }
    inFlight.current = true;
    setSaving(true);
    setError(null);
    try {
      await onSave(next);
      // Esc may have closed this editor while the save was in flight. Closing
      // again would shut an editor the user has since reopened and lose its
      // draft; the saved text shows once the record catches up.
      if (!closed.current) {
        close({ saved: next, keyboard });
      }
    } catch (caught) {
      setError(userFacingErrorMessage(caught, "Couldn't save."));
      (textareaRef.current ?? inputRef.current)?.focus();
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close({ keyboard: true });
    } else if (
      event.key === "Enter" &&
      (!multiline || event.metaKey || event.ctrlKey)
    ) {
      event.preventDefault();
      void save(true);
    }
  };

  const fieldProps = {
    "aria-label": ariaLabel,
    "aria-invalid": missing || error !== null,
    "aria-busy": saving,
    placeholder,
    value: draft,
    maxLength,
    autoFocus: true,
    readOnly: saving,
    onKeyDown,
    onBlur: () => void save(false),
  };

  const failed = missing || error !== null;
  return (
    <div className={classNames(styles.editor, { [styles.fill]: fill })}>
      {multiline ? (
        <div
          className={classNames(styles.surface, styles.field, {
            [styles.fieldError]: failed,
          })}
        >
          <textarea
            ref={textareaRef}
            className={classNames(styles.input, styles.textarea, textClass)}
            rows={1}
            onChange={(event) => {
              setDraft(event.target.value);
              setError(null);
            }}
            {...fieldProps}
          />
        </div>
      ) : (
        // The input sits over a hidden copy of its text, which sizes it,
        // unless the field fills its container.
        <span
          className={classNames(
            styles.surface,
            styles.field,
            fill ? styles.fillField : styles.sizer,
            textClass,
            { [styles.fieldError]: failed },
          )}
          data-value={draft || placeholder || ""}
        >
          <input
            ref={inputRef}
            className={classNames(styles.input, textClass)}
            onChange={(event) => {
              setDraft(event.target.value);
              setMissing(false);
              setError(null);
            }}
            {...fieldProps}
          />
        </span>
      )}
      {/* Only an error shows under the field, laid over what follows, so
          editing never moves the rest of the header. */}
      {(missing || error !== null) && (
        <div className={styles.hintRow}>
          {missing ? (
            <span className={styles.errorText} role="alert">
              {requiredMessage}
            </span>
          ) : (
            <span className={styles.errorText} role="alert">
              {error}{" "}
              <button
                type="button"
                className={styles.retry}
                // Keeps focus in the field, so clicking Retry isn't a blur.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => void save(true)}
              >
                Retry
              </button>{" "}
              · Esc to discard
            </span>
          )}
        </div>
      )}
    </div>
  );
}
